/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  CloseOutlined,
  CheckOutlined,
} from '@ant-design/icons'
import { Alert, Input as AntInput } from 'antd'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { TextInput } from 'src/components/ts-components/TextInput'
import Text from 'src/components/ts-components/Text'
import { CountryAndProvinceInput } from 'src/components/ts-components/CountryAndProvinceInput'
import CopyComponent from 'src/components/CopyComponent'
import DeleteOfficialModal from '../OfficialModal/DeleteOfficialModal'
import { useLocationContext } from './LocationContext'
import useWindowSize from 'src/hooks/useWindowSize'
import { Country } from '@sportninja/common/hooks/useCountries'
import { Province } from 'src/components/ts-components/ProvinceSelectInput'
import { isCanlan } from '@sportninja/common/utils/customer-name'

const CANADA_COUNTRY_ID = '36'
const USA_COUNTRY_ID = '226'

const fieldWrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const DeleteButton = ({ onClick }: { onClick?: () => void }) => {
  return (
    <button
      type='button'
      onClick={onClick}
      css={css`
        display: flex;
        padding: 8px 16px 8px 8px;
        justify-content: center;
        align-items: center;
        gap: 8px;
        border-radius: 8px;
        border: 1px solid ${colors.ERROR_200};
        background: ${colors.ERROR_200};
        box-shadow: 0px 2px 0px 0px rgba(5, 145, 255, 0.1);
        cursor: pointer;
        &:hover {
          opacity: 0.9;
        }
      `}
    >
      <DeleteOutlined
        css={css`
          font-size: 24px;
          color: ${colors.NEUTRAL_0};
        `}
        width={24}
        height={24}
      />
      <Text variant='heading6' weight='semiBold' color={colors.NEUTRAL_0}>
        REMOVE LOCATION
      </Text>
    </button>
  )
}

const LocationModalContent: React.FC = () => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])
  const { state, dispatch, isEdit, onDelete } = useLocationContext()
  const newFacilityInputRef = useRef<HTMLInputElement>(null)
  const editFacilityInputRef = useRef<HTMLInputElement>(null)
  const provincesRef = useRef<Province[]>([])
  const countriesRef = useRef<Country[]>([])
  const [provincesLoaded, setProvincesLoaded] = useState(0)

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const handleProvincesLoaded = useCallback((provinces: Province[]) => {
    provincesRef.current = provinces
    setProvincesLoaded((prev) => prev + 1)
  }, [])

  const handleFieldChange = useCallback(
    (field: string) => (value: string) => {
      dispatch({
        type: 'SET_FIELD',
        field: field as keyof typeof state,
        value,
      })
    },
    [dispatch]
  )

  const handleCountryChange = useCallback(
    (value: string | undefined, countryId: string | number | undefined) => {
      dispatch({
        type: 'SET_COUNTRY',
        isoCode: value || '',
        id: countryId ?? null,
      })
    },
    [dispatch]
  )

  const handleProvinceChange = useCallback(
    (value: string | undefined, provinceId: string | number | undefined) => {
      dispatch({
        type: 'SET_PROVINCE',
        isoCode: value || '',
        id: provinceId ?? null,
      })
    },
    [dispatch]
  )

  const handleAddFacility = useCallback(() => {
    if (state.newFacilityName.trim()) {
      dispatch({ type: 'ADD_FACILITY', name: state.newFacilityName.trim() })
    }
  }, [dispatch, state.newFacilityName])

  const handleNewFacilityKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleAddFacility()
      } else if (e.key === 'Escape') {
        dispatch({ type: 'TOGGLE_NEW_FACILITY_INPUT' })
      }
    },
    [dispatch, handleAddFacility]
  )

  const handleEditFacility = useCallback(
    (id: string) => {
      if (state.editingFacilityName.trim()) {
        dispatch({
          type: 'UPDATE_FACILITY',
          id,
          name: state.editingFacilityName.trim(),
        })
      } else {
        dispatch({ type: 'CANCEL_EDITING_FACILITY' })
      }
    },
    [dispatch, state.editingFacilityName]
  )

  const handleEditFacilityKeyDown = useCallback(
    (e: React.KeyboardEvent, id: string) => {
      if (e.key === 'Enter') {
        handleEditFacility(id)
      } else if (e.key === 'Escape') {
        dispatch({ type: 'CANCEL_EDITING_FACILITY' })
      }
    },
    [dispatch, handleEditFacility]
  )

  const provinceRequired =
    String(state.countryId) === CANADA_COUNTRY_ID ||
    String(state.countryId) === USA_COUNTRY_ID

  // Get country ISO code from ID for CountryAndProvinceInput
  const countryValueForInput = useMemo(() => {
    if (state.countryIsoCode) {
      return state.countryIsoCode
    }
    if (state.countryId && countriesRef.current.length > 0) {
      const country = countriesRef.current.find(
        (c) => String(c.id) === String(state.countryId)
      )
      return country?.iso_3166_2 || ''
    }
    return ''
  }, [state.countryIsoCode, state.countryId])

  // Get province ID as string for CountryAndProvinceInput
  // provincesLoaded triggers re-compute when provinces are loaded asynchronously
  const provinceValueForInput = useMemo(() => {
    if (state.provinceId || state.provinceName) {
      const province = provincesRef.current.find(
        (p) =>
          p.id === state.provinceId ||
          p?.iso_3166_2?.toLowerCase() === state.provinceName?.toLowerCase()
      )
      return province?.iso_3166_2 || ''
    }
    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.provinceId, state.provinceName, provincesLoaded])

  const handleDeleteClick = useCallback(() => {
    setDeleteError(null)
    setIsDeleteModalOpen(true)
  }, [])

  const handleDeleteModalClose = useCallback(() => {
    setIsDeleteModalOpen(false)
    setDeleteError(null)
  }, [])

  const handleDeleteConfirm = useCallback(async () => {
    try {
      setIsDeleting(true)
      setDeleteError(null)
      await onDelete()
      setIsDeleteModalOpen(false)
    } catch (e: unknown) {
      const error = e as { message?: string }
      setDeleteError(error?.message || 'Failed to delete location')
    } finally {
      setIsDeleting(false)
    }
  }, [onDelete])

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 16px;
        height: 100%;
        overflow-y: auto;
        width: 100%;
      `}
    >
      {/* Header with ID */}
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <Text variant='captionLarge' weight='semiBold'>
          {isCanlan ? 'Sport Complex' : 'Location'} Info
        </Text>
        {isEdit && state.id ? (
          <div>
            <CopyComponent id={state.id} />
          </div>
        ) : null}
      </div>

      {/* Name */}
      <div css={fieldWrapperStyle}>
        <TextInput
          name='name'
          label='Name'
          value={state.name}
          setValue={handleFieldChange('name')}
          fieldErrors={state.fieldErrors}
          customError={state.customError}
          required
          placeholder={
            isCanlan ? 'Enter Sport Complex Name' : 'Enter Location Name'
          }
        />
      </div>

      {/* Address Line 1 */}
      <div css={fieldWrapperStyle}>
        <TextInput
          name='address_line1'
          label='Address Line 1'
          value={state.address_line1}
          setValue={handleFieldChange('address_line1')}
          fieldErrors={state.fieldErrors}
          customError={state.customError}
          required
          placeholder='Street Address'
        />
      </div>

      {/* Address Line 2 */}
      <div css={fieldWrapperStyle}>
        <TextInput
          name='address_line2'
          label='Address Line 2'
          value={state.address_line2}
          setValue={handleFieldChange('address_line2')}
          fieldErrors={state.fieldErrors}
          customError={state.customError}
          placeholder='Apt, Suite, Unit, Etc. (Optional)'
        />
      </div>

      {/* City and Postal Code */}
      <div
        css={css`
          display: flex;
          gap: 16px;
          ${isMobile &&
          css`
            flex-direction: column;
          `}
        `}
      >
        <div
          css={css`
            flex: 1;
            ${fieldWrapperStyle}
          `}
        >
          <TextInput
            name='city'
            label='City'
            value={state.city}
            setValue={handleFieldChange('city')}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            required
            placeholder='City'
          />
        </div>
        <div
          css={css`
            flex: 1;
            ${fieldWrapperStyle}
          `}
        >
          <TextInput
            name='postcode'
            label='Postal Code'
            value={state.postcode}
            setValue={handleFieldChange('postcode')}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            required
            placeholder='Postal/ZIP Code'
          />
        </div>
      </div>

      {/* Country and Province */}
      <CountryAndProvinceInput
        countryValue={countryValueForInput}
        provinceValue={provinceValueForInput}
        onCountryChange={handleCountryChange}
        onProvinceChange={handleProvinceChange}
        valueMode='iso_code'
        provinceValueMode='iso_code'
        fieldErrors={{
          country: state.fieldErrors.includes('country'),
          province: state.fieldErrors.includes('province'),
        }}
        customErrors={{
          country: state.customError.country || undefined,
          province: state.customError.province || undefined,
        }}
        requiredFields={{
          country: true,
          province: provinceRequired,
        }}
        responsive={isMobile}
        countriesRef={countriesRef}
        provincesRef={provincesRef}
        initialCountryId={state.countryId}
        initialProvinceId={state.provinceId}
        onProvincesLoaded={handleProvincesLoaded}
      />

      {/* Separator above Facilities */}
      <div
        css={css`
          height: 1px;
          min-height: 1px;
          background: ${colors.SOFT_STEEL};
          width: 100%;
          flex-shrink: 0;
        `}
      />

      {/* Facilities Header */}
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <Text variant='captionLarge' weight='semiBold'>
          Facilities
        </Text>
      </div>

      {/* Facilities Section */}
      <div>
        {/* Facilities List */}
        {state.facilities.map((facility) => (
          <div
            key={facility.id}
            css={css`
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 8px 12px;
              background: ${colors.SECONDARY_200};
              border-radius: 8px;
              margin-bottom: 8px;
            `}
          >
            {state.editingFacilityId === facility.id ? (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  flex: 1;
                `}
              >
                <AntInput
                  ref={editFacilityInputRef as React.Ref<never>}
                  value={state.editingFacilityName}
                  onChange={(e) =>
                    dispatch({
                      type: 'SET_EDITING_FACILITY_NAME',
                      name: e.target.value,
                    })
                  }
                  onKeyDown={(e) => handleEditFacilityKeyDown(e, facility.id!)}
                  autoFocus
                  css={css`
                    flex: 1;
                    background: ${colors.SECONDARY_200};
                    border: 1px solid ${colors.SECONDARY_100};
                    color: ${colors.WHITE};
                    height: 40px;
                    border-radius: 8px;
                  `}
                />
                <button
                  type='button'
                  onClick={() => handleEditFacility(facility.id!)}
                  css={css`
                    background: ${colors.PRIMARY};
                    border: none;
                    border-radius: 8px;
                    height: 40px;
                    width: 40px;
                    cursor: pointer;
                    color: ${colors.WHITE};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    &:hover {
                      opacity: 0.8;
                    }
                  `}
                >
                  <CheckOutlined />
                </button>
                <button
                  type='button'
                  onClick={() => dispatch({ type: 'CANCEL_EDITING_FACILITY' })}
                  css={css`
                    background: transparent;
                    border: 1px solid ${colors.NEUTRAL_50};
                    border-radius: 8px;
                    height: 40px;
                    width: 40px;
                    cursor: pointer;
                    color: ${colors.NEUTRAL_50};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    &:hover {
                      opacity: 0.8;
                    }
                  `}
                >
                  <CloseOutlined />
                </button>
              </div>
            ) : (
              <Text
                variant='paragraphSmall'
                weight='regular'
                color={colors.WHITE}
              >
                {facility.name}
              </Text>
            )}

            {state.editingFacilityId !== facility.id && (
              <div
                css={css`
                  display: flex;
                  gap: 8px;
                `}
              >
                <button
                  type='button'
                  onClick={() =>
                    dispatch({
                      type: 'START_EDITING_FACILITY',
                      id: facility.id!,
                      name: facility.name,
                    })
                  }
                  css={css`
                    background: transparent;
                    border: none;
                    cursor: pointer;
                    color: ${colors.PRIMARY};
                    padding: 4px;
                    font-size: 18px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    &:hover {
                      opacity: 0.8;
                    }
                  `}
                >
                  <EditOutlined />
                </button>
                <button
                  type='button'
                  onClick={() =>
                    dispatch({ type: 'DELETE_FACILITY', id: facility.id! })
                  }
                  css={css`
                    background: transparent;
                    border: none;
                    cursor: pointer;
                    color: ${colors.ERROR_300};
                    padding: 4px;
                    font-size: 18px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    &:hover {
                      opacity: 0.8;
                    }
                  `}
                >
                  <DeleteOutlined />
                </button>
              </div>
            )}
          </div>
        ))}

        {/* New Facility Input */}
        {state.showNewFacilityInput ? (
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              margin-bottom: 8px;
            `}
          >
            <AntInput
              ref={newFacilityInputRef as React.Ref<never>}
              value={state.newFacilityName}
              onChange={(e) =>
                dispatch({
                  type: 'SET_NEW_FACILITY_NAME',
                  name: e.target.value,
                })
              }
              onKeyDown={handleNewFacilityKeyDown}
              placeholder='Enter Facility Name'
              autoFocus
              css={css`
                flex: 1;
                background: ${colors.SECONDARY_200};
                border: 1px solid ${colors.SECONDARY_100};
                color: ${colors.WHITE};
                height: 40px;
                border-radius: 8px;
              `}
            />
            <button
              type='button'
              onClick={handleAddFacility}
              disabled={!state.newFacilityName.trim()}
              css={css`
                background: ${colors.PRIMARY};
                border: none;
                border-radius: 8px;
                height: 40px;
                width: 40px;
                cursor: pointer;
                color: ${colors.WHITE};
                display: flex;
                align-items: center;
                justify-content: center;
                &:disabled {
                  opacity: 0.5;
                  cursor: not-allowed;
                }
              `}
            >
              <CheckOutlined />
            </button>
            <button
              type='button'
              onClick={() => dispatch({ type: 'TOGGLE_NEW_FACILITY_INPUT' })}
              css={css`
                background: transparent;
                border: 1px solid ${colors.NEUTRAL_50};
                border-radius: 8px;
                height: 40px;
                width: 40px;
                cursor: pointer;
                color: ${colors.NEUTRAL_50};
                display: flex;
                align-items: center;
                justify-content: center;
              `}
            >
              <CloseOutlined />
            </button>
          </div>
        ) : (
          <button
            type='button'
            onClick={() => dispatch({ type: 'TOGGLE_NEW_FACILITY_INPUT' })}
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              background: transparent;
              border: 1px dashed ${colors.NEUTRAL_50};
              border-radius: 8px;
              padding: 8px 16px;
              cursor: pointer;
              color: ${colors.NEUTRAL_50};
              width: 100%;
              justify-content: center;
              &:hover {
                border-color: ${colors.PRIMARY};
                color: ${colors.PRIMARY};
              }
            `}
          >
            <PlusOutlined />
            <span>Add Facility</span>
          </button>
        )}
      </div>

      {/* Danger Zone (only for edit mode) */}
      {isEdit && (
        <>
          {/* Separator above Danger Zone */}
          <div
            css={css`
              height: 1px;
              min-height: 1px;
              background: ${colors.SOFT_STEEL};
              width: 100%;
              flex-shrink: 0;
            `}
          />
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              flex: 1 0 0;
              width: 100%;
            `}
          >
            <Text
              variant='captionSmall'
              weight='semiBold'
              color={colors.NEUTRAL_50}
            >
              Danger Zone
            </Text>
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.WHITE}
            >
              Permanently Remove This {isCanlan ? 'Sport Complex' : 'Location'}{' '}
              From The {isCanlan ? 'League' : 'Organization'}.
            </Text>
            <DeleteButton onClick={handleDeleteClick} />
          </div>
        </>
      )}

      {/* Error Display */}
      {(state.error || state.customError.address) && (
        <Alert
          message={state.error || state.customError.address}
          type='error'
          showIcon
          css={css`
            background: ${colors.ERROR_50};
            border: 1px solid ${colors.ERROR_300};
            border-radius: 8px;
            margin-top: 8px;
            .ant-alert-message {
              color: ${colors.ERROR_300};
            }
            .ant-alert-icon {
              color: ${colors.ERROR_300};
            }
          `}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteOfficialModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteModalClose}
        onConfirm={handleDeleteConfirm}
        officialName={state.name}
        isLoading={isDeleting}
        error={deleteError}
        title={`Remove ${isCanlan ? 'Sport Complex' : 'Location'}`}
        message={`Are You Sure You Want To Remove ${state.name}? This Action Cannot Be Undone.`}
      />
    </div>
  )
}

export default LocationModalContent
